import { useTranslation } from 'react-i18next';

import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import type { OrderPublicStatus } from '~/api/Order';

type BadgeSize = 'sm' | 'md';

interface OrderStatusBadgeV3Props {
  status: OrderPublicStatus;
  size?: BadgeSize;
  className?: string;
}

type BadgeTone = 'success' | 'warning' | 'error' | 'neutral';

const STATUS_TONE: Record<OrderPublicStatus, BadgeTone> = {
  accepted: 'neutral',
  picking: 'neutral',
  ready_to_ship: 'neutral',
  ready_for_pickup: 'neutral',
  in_delivery: 'neutral',
  delivered: 'success',
  failed: 'error',
  cancelled: 'error',
  not_collected: 'error',
};

const TONE_CLASSES: Record<BadgeTone, string> = {
  success: 'bg-system-success-subtle text-system-success',
  warning: 'bg-system-warning-subtle text-system-warning',
  error: 'bg-system-error-subtle text-system-error',
  neutral: 'bg-surface-transparent-2 text-text-secondary',
};

const SIZE_CLASSES: Record<BadgeSize, string> = {
  sm: 'h-4 px-1.5 py-0.5',
  md: 'h-6 px-3 py-1',
};

const STATUS_KEY: Record<OrderPublicStatus, string> = {
  accepted: 'accepted',
  picking: 'picking',
  ready_to_ship: 'readyToShip',
  ready_for_pickup: 'readyForPickup',
  in_delivery: 'inDelivery',
  delivered: 'delivered',
  failed: 'failed',
  cancelled: 'cancelled',
  not_collected: 'notCollected',
};

const OrderStatusBadgeV3 = ({
  status,
  size = 'md',
  className,
}: OrderStatusBadgeV3Props) => {
  const { t } = useTranslation();
  const tone = STATUS_TONE[status];

  const Label =
    size === 'sm' ? Typography.Text50Medium : Typography.Text200Medium;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full',
        SIZE_CLASSES[size],
        TONE_CLASSES[tone],
        className,
      )}
    >
      <Label as="span">{t(STATUS_KEY[status])}</Label>
    </span>
  );
};

export default OrderStatusBadgeV3;
